/**
 * Cost Dashboard — Token spend analytics.
 */

import { useQuery } from "@tanstack/react-query";
import {
  AreaChart, Area, BarChart, Bar, ResponsiveContainer,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { CorpoCard } from "../components/ui/CorpoCard";
import { CRTHeader } from "../components/ui/CRTHeader";
import { HexBadge } from "../components/ui/HexBadge";
import { DollarSign, TrendingUp, Cpu, Zap } from "lucide-react";

interface CostSummary {
  total_cost: number; total_input: number; total_output: number;
  cost_today: number; cost_7d: number; cost_30d: number;
  sessions_total: number; runs_total: number;
}

interface DailyRow {
  date: string; chat_cost: number; agent_cost: number;
  total_cost: number; sessions: number; runs: number;
}

interface AgentRow {
  agent: string; cost: number; sessions: number;
  tokens_in: number; tokens_out: number;
}

function fmt(n: number): string { return `$${n.toFixed(2)}`; }
function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function CostsRoute() {
  const { data: summary } = useQuery<CostSummary>({
    queryKey: ["costs", "summary"],
    queryFn: () => fetch("/api/costs/summary").then((r) => r.json()),
  });

  const { data: dailyData } = useQuery<{ daily: DailyRow[] }>({
    queryKey: ["costs", "daily"],
    queryFn: () => fetch("/api/costs/daily?days=30").then((r) => r.json()),
  });

  const { data: agentData } = useQuery<{ agents: AgentRow[] }>({
    queryKey: ["costs", "by-agent"],
    queryFn: () => fetch("/api/costs/by-agent").then((r) => r.json()),
  });

  const daily = dailyData?.daily ?? [];
  const agents = agentData?.agents ?? [];

  return (
    <div className="p-4 md:p-6 space-y-6">
      <CRTHeader text="Costos" level={2} />

      {/* Summary cards */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <CorpoCard>
          <div className="flex items-center gap-3">
            <DollarSign size={20} className="text-[var(--bw-warn)]" />
            <div>
              <p className="text-xs text-[var(--bw-muted)] uppercase">Hoy</p>
              <p className="text-2xl font-bold text-[var(--bw-fg)]">{fmt(summary?.cost_today ?? 0)}</p>
            </div>
          </div>
        </CorpoCard>
        <CorpoCard>
          <div>
            <p className="text-xs text-[var(--bw-muted)] uppercase">7 días</p>
            <p className="text-2xl font-bold text-[var(--bw-fg)]">{fmt(summary?.cost_7d ?? 0)}</p>
          </div>
        </CorpoCard>
        <CorpoCard>
          <div>
            <p className="text-xs text-[var(--bw-muted)] uppercase">30 días</p>
            <p className="text-2xl font-bold text-[var(--bw-fg)]">{fmt(summary?.cost_30d ?? 0)}</p>
          </div>
        </CorpoCard>
        <CorpoCard>
          <div>
            <p className="text-xs text-[var(--bw-muted)] uppercase">Total</p>
            <p className="text-2xl font-bold text-[var(--bw-accent)]">{fmt(summary?.total_cost ?? 0)}</p>
            <p className="text-[10px] text-[var(--bw-muted)] mt-1">
              {fmtTokens(summary?.total_input ?? 0)} in / {fmtTokens(summary?.total_output ?? 0)} out
            </p>
          </div>
        </CorpoCard>
      </div>

      {/* Daily spend chart */}
      <CorpoCard title="GASTO DIARIO (30D)">
        {daily.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={daily}>
              <defs>
                <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--bw-warn)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="var(--bw-warn)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--bw-border)" />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: "var(--bw-muted)" }} tickFormatter={(d: string) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: "var(--bw-muted)" }} tickFormatter={(v: number) => `$${v.toFixed(2)}`} />
              <Tooltip
                contentStyle={{ background: "var(--bw-surface)", border: "1px solid var(--bw-border)", color: "var(--bw-fg)", fontSize: 11 }}
                formatter={(v) => [`$${Number(v ?? 0).toFixed(3)}`, ""]}
              />
              <Area type="monotone" dataKey="chat_cost" stackId="1" stroke="var(--bw-accent)" fill="var(--bw-accent)" fillOpacity={0.2} name="Chat" />
              <Area type="monotone" dataKey="agent_cost" stackId="1" stroke="var(--bw-warn)" fill="url(#costGrad)" name="Agents" />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-[var(--bw-muted)] text-center py-8">Sin datos</p>
        )}
      </CorpoCard>

      {/* By agent */}
      <CorpoCard title="COSTO POR AGENTE">
        {agents.length > 0 ? (
          <div className="space-y-2">
            {agents.map((a) => (
              <div key={a.agent} className="flex items-center justify-between py-1 border-b border-[var(--bw-border)] last:border-0">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Cpu size={14} className="text-[var(--bw-accent)] flex-shrink-0" />
                  <span className="text-sm text-[var(--bw-fg)] truncate">{a.agent}</span>
                  <HexBadge variant="muted" size="sm">{a.sessions} ses</HexBadge>
                </div>
                <div className="text-right">
                  <span className="text-sm font-bold text-[var(--bw-warn)]">{fmt(a.cost)}</span>
                  <p className="text-[10px] text-[var(--bw-muted)]">
                    {fmtTokens(a.tokens_in)}↓ {fmtTokens(a.tokens_out)}↑
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[var(--bw-muted)] text-center py-4">Sin datos</p>
        )}
      </CorpoCard>
    </div>
  );
}
